/**
 * Fantasy Console 0 - SFX Binary Encoding/Decoding
 *
 * Binary format per SFX (131 bytes):
 *   Byte 0: speed (1-255)
 *   Byte 1: loop_start (0-31)
 *   Byte 2: loop_end (0-31)
 *   Bytes 3-130: 32 notes × 4 bytes each [pitch, waveform, volume, effect]
 *
 * Total: 64 SFX × 131 bytes = 8,384 bytes → base64
 */

import type { SFX, SFXNote } from '@fc0/types'
import {
  SFX_COUNT,
  NOTES_PER_SFX,
  BYTES_PER_SFX,
  SFX_BANK_BYTES,
  SFX_HEADER_BYTES,
} from '@fc0/types'

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.floor(value)))
}

/**
 * Create an empty SFX note (silent)
 */
export function createEmptyNote(): SFXNote {
  return { pitch: 0, waveform: 0, volume: 0, effect: 0 }
}

/**
 * Create an empty SFX with 32 silent notes
 */
export function createEmptySFX(): SFX {
  const notes: SFXNote[] = []
  for (let i = 0; i < NOTES_PER_SFX; i++) {
    notes.push(createEmptyNote())
  }
  return {
    speed: 1,
    loop_start: 0,
    loop_end: 0,
    notes,
  }
}

/**
 * Create a bank of 64 empty SFX
 */
export function createEmptySFXBank(): SFX[] {
  const bank: SFX[] = []
  for (let i = 0; i < SFX_COUNT; i++) {
    bank.push(createEmptySFX())
  }
  return bank
}

/**
 * Encode an array of 64 SFX into a base64 string
 */
export function encodeSFX(sfxArray: SFX[]): string {
  const buffer = new Uint8Array(SFX_BANK_BYTES)

  for (let i = 0; i < SFX_COUNT; i++) {
    const sfx = i < sfxArray.length ? sfxArray[i] : createEmptySFX()
    const offset = i * BYTES_PER_SFX

    // Header bytes
    buffer[offset] = clamp(sfx.speed, 1, 255)
    buffer[offset + 1] = clamp(sfx.loop_start, 0, 31)
    buffer[offset + 2] = clamp(sfx.loop_end, 0, 31)

    // Note bytes
    for (let n = 0; n < NOTES_PER_SFX; n++) {
      const note = n < sfx.notes.length ? sfx.notes[n] : createEmptyNote()
      const noteOffset = offset + SFX_HEADER_BYTES + n * 4

      buffer[noteOffset] = clamp(note.pitch, 0, 63)
      buffer[noteOffset + 1] = clamp(note.waveform, 0, 7)
      buffer[noteOffset + 2] = clamp(note.volume, 0, 7)
      buffer[noteOffset + 3] = clamp(note.effect, 0, 7)
    }
  }

  // Convert to base64
  let binary = ''
  for (let i = 0; i < buffer.length; i++) {
    binary += String.fromCharCode(buffer[i])
  }
  return btoa(binary)
}

/**
 * Decode a base64 string into an array of 64 SFX
 * Returns empty bank on invalid input
 */
export function decodeSFX(base64: string | null | undefined): SFX[] {
  if (!base64) {
    return createEmptySFXBank()
  }

  let buffer: Uint8Array
  try {
    const binary = atob(base64)
    buffer = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      buffer[i] = binary.charCodeAt(i)
    }
  } catch {
    return createEmptySFXBank()
  }

  if (buffer.length !== SFX_BANK_BYTES) {
    return createEmptySFXBank()
  }

  const sfxArray: SFX[] = []

  for (let i = 0; i < SFX_COUNT; i++) {
    const offset = i * BYTES_PER_SFX

    const speed = Math.max(1, Math.min(255, buffer[offset]))
    const loop_start = Math.min(31, buffer[offset + 1])
    const loop_end = Math.min(31, buffer[offset + 2])

    const notes: SFXNote[] = []
    for (let n = 0; n < NOTES_PER_SFX; n++) {
      const noteOffset = offset + SFX_HEADER_BYTES + n * 4
      notes.push({
        pitch: Math.min(63, buffer[noteOffset]),
        waveform: Math.min(7, buffer[noteOffset + 1]),
        volume: Math.min(7, buffer[noteOffset + 2]),
        effect: Math.min(7, buffer[noteOffset + 3]),
      })
    }

    sfxArray.push({ speed, loop_start, loop_end, notes })
  }

  return sfxArray
}
